import React from "react";
import { Container, Row, Col, Card, Button, Badge } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useFadeIn } from "../App";
import SafeVideo from "../components/SafeVideo";
import ShapeDivider from "../components/ShapeDivider";
import { getImageById } from "../data/wildlifeGallery";

const Team = () => {
  const [heroRef, heroVisible] = useFadeIn();
  const [founderRef, founderVisible] = useFadeIn();
  const [teamRef, teamVisible] = useFadeIn();
  const [videosRef, videosVisible] = useFadeIn();
  const [joinRef, joinVisible] = useFadeIn();

  const boardMembers = [
    {
      name: "Jemma Laing",
      role: "Chair of the Board",
      description:
        "Jemma provides strategic leadership and governance oversight, ensuring our mission remains focused on sustainable wildlife conservation. Her business acumen and commitment to ethical practices guide our organisational development.",
      expertise: [
        "Strategic Leadership",
        "Corporate Governance",
        "Business Development",
      ],
      image:
        "https://cdn.builder.io/api/v1/image/assets%2F35ffb80beae24a4483ace4ab1c92b206%2F3401e7891389488b86c087789edda917?format=webp&width=2560&quality=95",
    },
    {
      name: "Caroline Tierney",
      role: "Treasurer",
      description:
        "Caroline oversees our financial management and ensures responsible stewardship of donor funds. Her expertise in financial planning helps maximise our impact while maintaining transparent accountability to our supporters.",
      expertise: [
        "Financial Management",
        "Budget Planning",
        "Compliance & Reporting",
      ],
      image:
        "https://cdn.builder.io/api/v1/image/assets%2F35ffb80beae24a4483ace4ab1c92b206%2Fd7b7334a083c434a97816f56e9cd034d?format=webp&width=2560&quality=95",
    },
    {
      name: "Emma Senior",
      role: "Secretary",
      description:
        "Emma brings valuable legal expertise to our board, with a background in conveyancing, wills and estates, and criminal law. Her legal knowledge strengthens our governance framework and ensures compliance with regulatory requirements.",
      expertise: [
        "Legal Compliance",
        "Governance & Documentation",
        "Risk Management",
      ],
      image:
        "https://cdn.builder.io/api/v1/image/assets%2F35ffb80beae24a4483ace4ab1c92b206%2F18686b6869b9406bb84b1366df04e506?format=webp&width=2560&quality=95",
    },
    {
      name: "Julie Ferguson",
      role: "Board Director",
      description:
        "Julie contributes extensive experience in community services and wildlife protection. Her dedication to animal welfare and understanding of conservation challenges helps shape our programmes and community engagement strategies.",
      expertise: [
        "Community Engagement",
        "Wildlife Protection",
        "Programme Development",
      ],
      image:
        "https://cdn.builder.io/api/v1/image/assets%2F35ffb80beae24a4483ace4ab1c92b206%2Ffc2c880c56374e95b3d31cd8d42fb558?format=webp&width=2560&quality=95",
    },
    {
      name: "Devon Cuimara",
      role: "Board Director",
      description:
        "Devon brings valuable insights from his background in community services and Indigenous affairs. His commitment to ethical wildlife care and cultural perspectives enriches our approach to conservation and community partnerships.",
      expertise: [
        "Community Relations",
        "Cultural Awareness",
        "Conservation Ethics",
      ],
      image:
        "https://cdn.builder.io/api/v1/image/assets%2F35ffb80beae24a4483ace4ab1c92b206%2F40f6e63257994b43bbd2e784e5bad67a?format=webp&width=2560&quality=95",
    },
  ];

  return (
    <>
      {/* Hero Section */}
      <section
        ref={heroRef}
        className={`hero-team-section fade-in-section ${heroVisible ? "is-visible" : ""}`}
        style={{
          backgroundImage: `linear-gradient(rgba(74, 92, 45, 0.8), rgba(141, 191, 75, 0.8)), url(${getImageById('tawny-frogmouth').url})`,
          padding: "120px 0 80px",
          color: "white",
          position: "relative",
          overflow: "hidden",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed"
        }}
      >
        <div
          className="hero-pattern"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage:
              'url("data:image/svg+xml,%3Csvg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="%23ffffff" fill-opacity="0.06"%3E%3Cpath d="M20 20c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10zm10 0c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10z"/%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.3,
          }}
        ></div>
        <Container className="position-relative">
          <Row className="justify-content-center text-center">
            <Col lg={10}>
              <h1 className="display-3 fw-bold mb-4" style={{ color: "white" }}>
                Meet Our Team
              </h1>
              <p
                className="lead mb-5"
                style={{ fontSize: "1.3rem", opacity: 0.95 }}
              >
                The passionate individuals dedicated to Australia's wildlife
                conservation and rehabilitation
              </p>
            </Col>
          </Row>
        </Container>
        <ShapeDivider color="white" />
      </section>

      {/* Founder Section */}
      <section
        ref={founderRef}
        className={`founder-section fade-in-section ${founderVisible ? "is-visible" : ""}`}
        style={{ padding: "100px 0" }}
      >
        <Container>
          <Row className="align-items-center g-5">
            <Col lg={5}>
              <div className="founder-image-container position-relative">
                <div
                  className="founder-bg"
                  style={{
                    background:
                      "linear-gradient(135deg, var(--primary-yellow) 0%, #e6b73e 100%)",
                    borderRadius: "50%",
                    width: "350px",
                    height: "350px",
                    position: "absolute",
                    top: "20px",
                    left: "20px",
                    zIndex: "1",
                  }}
                ></div>
                <img
                  src="https://cdn.builder.io/api/v1/image/assets%2F35ffb80beae24a4483ace4ab1c92b206%2F35d55aceac2c4cc58342e05767e52e26?format=webp&width=2560&quality=95"
                  alt="Jenni Abbott - CEO & Founder"
                  className="img-fluid rounded-circle shadow-lg position-relative"
                  style={{
                    width: "350px",
                    height: "350px",
                    objectFit: "cover",
                    zIndex: "2",
                    border: "6px solid white",
                  }}
                />
              </div>
            </Col>
            <Col lg={7}>
              <div className="founder-content">
                <Badge
                  className="mb-3 px-3 py-2"
                  style={{
                    background: "var(--primary-yellow)",
                    fontSize: "0.9rem",
                    borderRadius: "50px",
                  }}
                >
                  CEO & Founder
                </Badge>
                <h2
                  className="mb-4"
                  style={{ color: "var(--primary-green)", fontSize: "2.5rem" }}
                >
                  Jenni Abbott
                </h2>
                <p
                  className="lead mb-4"
                  style={{ fontSize: "1.2rem", lineHeight: "1.8" }}
                >
                  With eleven years of hands-on experience in wildlife care, Jenni
                  founded Aussie Wildlife Limited from a determined vision to
                  provide sustainable care for Australia's wildlife.
                </p>
                <p
                  style={{
                    fontSize: "1.1rem",
                    lineHeight: "1.7",
                    color: "var(--dark-text)",
                    marginBottom: "2rem",
                  }}
                >
                  Recognising the need for long-term solutions in wildlife care,
                  Jenni created the organisation to promote ethical and
                  effective conservation practices. Her leadership has grown our
                  organisation into a thriving network supported by a diverse
                  board of directors with backgrounds in wildlife care, health,
                  business, and finance.
                </p>
                <div className="founder-stats">
                  <Row>
                    <Col md={4} className="text-center mb-3">
                      <h4
                        className="fw-bold"
                        style={{ color: "var(--primary-green)" }}
                      >
                        10,000+
                      </h4>
                      <p className="text-muted">animals rehabilitated</p>
                    </Col>
                    <Col md={4} className="text-center mb-3">
                      <h4
                        className="fw-bold"
                        style={{ color: "var(--primary-green)" }}
                      >
                        300+
                      </h4>
                      <p className="text-muted">Rescues</p>
                    </Col>
                    <Col md={4} className="text-center mb-3">
                      <h4
                        className="fw-bold"
                        style={{ color: "var(--primary-green)" }}
                      >
                        50+
                      </h4>
                      <p className="text-muted">Release Sites</p>
                    </Col>
                  </Row>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Team Members Section */}
      <section
        ref={teamRef}
        className={`team-members-section section-bg fade-in-section ${teamVisible ? "is-visible" : ""}`}
        style={{ padding: "100px 0" }}
      >
        <Container>
          <Row className="text-center mb-5">
            <Col lg={8} className="mx-auto">
              <h2
                className="mb-4"
                style={{ color: "var(--primary-green)", fontSize: "2.5rem" }}
              >
                Our Board of Directors
              </h2>
              <p
                className="lead"
                style={{ fontSize: "1.2rem", color: "var(--dark-text)" }}
              >
                Our dedicated board of directors brings diverse expertise from legal, financial, and community service backgrounds.
                Together with our 14 team members, we work tirelessly to advance wildlife conservation across Queensland.
              </p>
            </Col>
          </Row>

          <Row className="g-4 justify-content-center">
            {boardMembers.map((member, index) => (
              <Col lg={6} xl={4} key={index}>
                <Card
                  className="team-card h-100 border-0 shadow-lg"
                  style={{
                    borderRadius: "20px",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  }}
                >
                  <Card.Body className="p-4 text-center">
                    <div className="team-image-container mb-3">
                      <img
                        src={member.image}
                        alt={`${member.name} - ${member.role}`}
                        className="img-fluid rounded-circle shadow"
                        style={{
                          width: "150px",
                          height: "150px",
                          objectFit: "cover",
                          objectPosition: "center top",
                          border: "4px solid var(--primary-yellow)",
                        }}
                      />
                    </div>
                    <h4
                      className="fw-bold mb-2"
                      style={{ color: "var(--primary-green)" }}
                    >
                      {member.name}
                    </h4>
                    <Badge
                      className="mb-3 px-3 py-2"
                      style={{
                        background: "var(--accent-green)",
                        fontSize: "0.85rem",
                        borderRadius: "50px",
                      }}
                    >
                      {member.role}
                    </Badge>
                    <p
                      className="text-muted mb-3 text-start"
                      style={{ fontSize: "0.95rem", lineHeight: "1.6" }}
                    >
                      {member.description}
                    </p>
                    <div className="expertise-tags">
                      {member.expertise.map((skill, skillIndex) => (
                        <span
                          key={skillIndex}
                          className="badge me-2 mb-2"
                          style={{
                            background: "rgba(74, 92, 45, 0.1)",
                            color: "var(--primary-green)",
                            fontSize: "0.75rem",
                            fontWeight: "normal",
                            borderRadius: "12px",
                            padding: "0.4rem 0.8rem",
                          }}
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Team in Action Videos */}
      <section
        ref={videosRef}
        className={`team-videos-section fade-in-section ${videosVisible ? "is-visible" : ""}`}
        style={{ padding: "100px 0", background: "var(--light-bg)" }}
      >
        <Container>
          <Row className="text-center mb-5">
            <Col lg={8} className="mx-auto">
              <h2
                className="mb-4"
                style={{ color: "var(--primary-green)", fontSize: "2.5rem" }}
              >
                Our Team in Action
              </h2>
              <p
                className="lead"
                style={{ fontSize: "1.2rem", color: "var(--dark-text)" }}
              >
                Go behind the scenes with our dedicated team members as they work tirelessly
                to provide the best possible care for Australia's wildlife
              </p>
            </Col>
          </Row>
          <Row className="g-5 justify-content-center">
            {[
              "https://cdn.builder.io/o/assets%2F35ffb80beae24a4483ace4ab1c92b206%2F422c87f0e3b14b2e8927be2de0f6c170?alt=media&token=2a14d631-7a00-443a-8379-6cacddbe29b2&apiKey=35ffb80beae24a4483ace4ab1c92b206",
              "https://cdn.builder.io/o/assets%2F35ffb80beae24a4483ace4ab1c92b206%2Fb48d9206c20244648688f458d601f3fa?alt=media&token=80ffb810-27e3-4235-b609-af08c3694acb&apiKey=35ffb80beae24a4483ace4ab1c92b206",
              "https://cdn.builder.io/o/assets%2F35ffb80beae24a4483ace4ab1c92b206%2Fd739f9840c624880aa5275134b357ff4?alt=media&token=ea51a656-8b8f-4276-b551-4025145bd963&apiKey=35ffb80beae24a4483ace4ab1c92b206"
            ].map((videoUrl, index) => (
              <Col lg={4} key={index}>
                <div
                  className="video-container shadow-lg"
                  style={{
                    borderRadius: "25px",
                    overflow: "hidden",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  }}
                >
                  <SafeVideo
                    src={videoUrl}
                    controls
                    className="w-100"
                    style={{ aspectRatio: "16/9", objectFit: "cover" }}
                    preload="metadata"
                    autoGeneratePoster={true}
                  />
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Join Our Team Section */}
      <section
        ref={joinRef}
        className={`join-team-section fade-in-section ${joinVisible ? "is-visible" : ""}`}
        style={{ padding: "100px 0" }}
      >
        <Container>
          <Row className="justify-content-center">
            <Col lg={8}>
              <Card
                className="join-card border-0 shadow-lg text-center"
                style={{
                  borderRadius: "25px",
                  background:
                    "linear-gradient(135deg, var(--primary-green) 0%, var(--accent-green) 100%)",
                  color: "white",
                  padding: "3rem 2rem",
                }}
              >
                <Card.Body>
                  <div
                    className="join-icon mb-4"
                    style={{
                      width: "80px",
                      height: "80px",
                      background: "rgba(255, 255, 255, 0.2)",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto",
                      fontSize: "2.5rem",
                    }}
                  >
                    <i className="fas fa-users"></i>
                  </div>
                  <h3
                    className="mb-4"
                    style={{
                      fontSize: "2.2rem",
                      fontWeight: "bold",
                      color: "white",
                    }}
                  >
                    Join Our Mission
                  </h3>
                  <p
                    className="lead mb-4"
                    style={{ fontSize: "1.2rem", opacity: 0.9 }}
                  >
                    Are you passionate about wildlife conservation? We're always
                    looking for dedicated volunteers and team members to join
                    our cause.
                  </p>
                  <p className="mb-5" style={{ opacity: 0.8 }}>
                    Whether you're an experienced carer, a student wanting to
                    learn, or someone who wants to make a difference, there's a
                    place for you in our team.
                  </p>
                  <div className="d-flex justify-content-center gap-3 flex-wrap">
                    <Button
                      as={Link}
                      to="/contact"
                      variant="light"
                      size="lg"
                      className="px-4"
                      style={{ borderRadius: "50px", fontWeight: "600" }}
                    >
                      <i className="fas fa-envelope me-2"></i>
                      Contact Us
                    </Button>
                    <Button
                      as={Link}
                      to="/what-we-do"
                      variant="outline-light"
                      size="lg"
                      className="px-4"
                      style={{ borderRadius: "50px", fontWeight: "600" }}
                    >
                      <i className="fas fa-info-circle me-2"></i>
                      Learn More
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default Team;
